import React, { useEffect, useState } from "react";
import { FaRedo, FaSearch, FaBook, FaShoppingCart, FaCopy, FaPlus } from "react-icons/fa";
import "../styles/Meals.css";
import Recipe from "../components/Recipe";
import { useRecipes } from "../hooks/useRecipes";
import { getRecipesByMeal } from "../services/recipeService";
import { signOutUser } from "../services/authService";
import AuthComponent from '../components/AuthComponent';
import AddRecipeForm from '../components/AddRecipeForm';
import { useAuth } from '../hooks/useAuth.ts';
import { MealSlot, RecipeData, Ingredient, GroceryItem, initialMealSlots } from '../types/meals.ts';
import { 
  getRandomRecipeFromCategory, 
  generateGroceryList, 
  copyGroceryListToClipboard,
  calculateTotalCalories,
  calculateTotalProtein,
  getAvailableRecipes,
  getFilteredRecipes
} from '../utils/mealHelpers.ts';



// ✅ MealSlot Card Component
const MealSlotCard: React.FC<{
  slot: MealSlot;
  index: number;
  activeDropdown: number | null;
  searchTerms: { [key: number]: string };
  onToggleDropdown: (index: number) => void;
  onSearchChange: (index: number, value: string) => void;
  onShuffleMeal: (index: number) => void;
  onViewRecipe: (recipe: RecipeData) => void;
  onSelectRecipe: (index: number, recipe: RecipeData) => void;
  useOwnData: boolean;
}> = ({ 
  slot, 
  index, 
  activeDropdown, 
  searchTerms, 
  onToggleDropdown, 
  onSearchChange, 
  onShuffleMeal, 
  onViewRecipe, 
  onSelectRecipe, 
  useOwnData 
}) => {
  const [availableRecipes, setAvailableRecipes] = useState<RecipeData[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAvailableRecipes = async (searchTerm: string = '') => {
    setLoading(true);
    try {
      const recipes = await getRecipesByMeal(
        slot.name.toLowerCase() as 'breakfast' | 'lunch' | 'snack' | 'dinner', 
        useOwnData
      );
      
      const filtered = searchTerm
        ? recipes.filter(recipe => recipe.title.toLowerCase().includes(searchTerm.toLowerCase()))
        : recipes;
      
      setAvailableRecipes(filtered);
    } catch (error) {
      console.error('Error loading recipes:', error);
      setAvailableRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeDropdown === index) {
      loadAvailableRecipes(searchTerms[index] || '');
    }
  }, [activeDropdown, index, searchTerms, useOwnData]);

  return (
    <div className="meal-card">
      <div className="meal-card-header">
        <h2>{slot.name}</h2>
        <div className="meal-buttons">
          {slot.recipe && (
            <button
              className="shuffle"
              onClick={() => onViewRecipe(slot.recipe!)}
              title="View Recipe"
              type="button"
            >
              <FaBook />
            </button>
          )}
          <button
            className="shuffle"
            onClick={() => onToggleDropdown(index)}
            title="Search Recipes"
            type="button"
          >
            <FaSearch />
          </button>
          <button
            className="shuffle"
            onClick={() => onShuffleMeal(index)}
            title="Shuffle Recipe"
            type="button"
          >
            <FaRedo />
          </button>
        </div>
      </div>

      {/* Search Dropdown */}
      {activeDropdown === index && (
        <div className="search-dropdown">
          <div className="search-input-container">
            <input
              type="text"
              placeholder="Search recipes..."
              value={searchTerms[index] || ""}
              onChange={(e) => {
                onSearchChange(index, e.target.value);
                loadAvailableRecipes(e.target.value);
              }}
              className="search-input"
              autoFocus
            />
          </div>
          <div className="search-results">
            {loading ? (
              <div className="search-loading">Loading recipes...</div>
            ) : availableRecipes.length > 0 ? (
              availableRecipes.map((recipe, optionIdx) => (
                <button
                  key={optionIdx}
                  onClick={() => onSelectRecipe(index, recipe)}
                  className="search-result-item"
                  type="button"
                >
                  <div className="search-result-title">{recipe.title}</div>
                  <div className="search-result-details">
                    {recipe.calories} kcal • {recipe.protein}g protein
                  </div>
                </button>
              ))
            ) : (
              <div className="no-recipes">
                No {slot.name.toLowerCase()} recipes found. 
                <br />
                <small>Add some recipes to get started!</small>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="meal-image-placeholder">
        {slot.recipe ? (
          <a 
            href={slot.recipe.link} 
            target="_blank"
            className="meal-image-text"
            rel="noopener noreferrer"
          >
            {slot.recipe.title}
          </a>
        ) : (
          <span className="meal-image-text">No recipe selected</span>
        )}
      </div>
      <p className="meal-details">
        {slot.recipe?.calories || 0} kcal <br /> 
        {slot.recipe?.protein || 0} g protein
      </p>
    </div>
  );
};

// ✅ Reusable Progress Ring Component
const ProgressRing: React.FC<{
  value: number;
  goal: number;
  color: string;
  label: string;
}> = ({ value, goal, color, label }) => {
  const radius = 80;
  const strokeWidth = 15;
  const normalizedRadius = radius - strokeWidth * 0.5;
  const circumference = normalizedRadius * 2 * Math.PI;
  const percent = Math.min(100, Math.round((value / goal) * 100));
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <div className="progress-ring-card">
      <svg height={radius * 2} width={radius * 2} data-color={color}>
        <circle
          className="progress-bg-circle"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          className="progress-circle"
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference + " " + circumference}
          strokeDashoffset={strokeDashoffset}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="progress-ring-text"
        >
          {value}/{goal}
        </text>
      </svg>
      <p className="progress-ring-label">{label}</p>
    </div>
  );
};

export default function MealPlanner() {
  const [mealSlots, setMealSlots] = useState<MealSlot[]>(initialMealSlots);
  const [goalCalories, setGoalCalories] = useState(2000);
  const [goalProtein, setGoalProtein] = useState(120);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeData | null>(null);
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [useOwnData, setUseOwnData] = useState(false);
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showManageRecipes, setShowManageRecipes] = useState(false);
  const [mealFilter, setMealFilter] = useState<string>('all');
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);
  const [editingRecipe, setEditingRecipe] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<RecipeData>>({});
  
  // Use authentication
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  // Use Firestore recipes - default data or user-specific
  const shouldFetchRecipes = useOwnData ? isAuthenticated : true;
  const { recipes: recipesData, loading: recipesLoading, error: recipesError } = useRecipes(shouldFetchRecipes, useOwnData);

  const totalCalories = calculateTotalCalories(mealSlots);
  const totalProtein = calculateTotalProtein(mealSlots);

  const handleLogout = async () => {
    try {
      await signOutUser();
      setUseOwnData(false); // Reset to default data after logout
      setShowAuthModal(false); // Hide auth modal after logout
      setMealSlots(initialMealSlots); // Clear meal selections when switching to default data
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleUseOwnDataToggle = () => {
    if (!useOwnData && !user) {
      // User wants to use their own data but isn't signed in
      setShowAuthModal(true);
      return;
    }
    setUseOwnData(!useOwnData);
    // Clear current meal selections when switching data sources
    setMealSlots(initialMealSlots);
  };

  useEffect(() => {
    // when user clicks outside the dropdown, close it
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown') && !target.closest('.meal-buttons')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Close auth modal when user successfully logs in and switch to user data
  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
      setUseOwnData(true); // Automatically switch to user's data after sign in
      setMealSlots(initialMealSlots); // Clear meal selections when switching to user data
    }
  }, [user, showAuthModal]);

  // Set useOwnData to true when user is logged in (handles page refresh)
  useEffect(() => {
    if (user && !showAuthModal) {
      setUseOwnData(true);
    }
  }, [user, showAuthModal]);



  const shuffleMeal = async (index: number) => {
    const updated = [...mealSlots];
    const currentSlot = updated[index];
    const randomRecipe = await getRandomRecipeFromCategory(currentSlot.name, useOwnData);
    
    updated[index] = {
      ...updated[index],
      recipe: randomRecipe,
    };
    setMealSlots(updated);
  };

  const randomizeDay = async () => {
    const updatedSlots = await Promise.all(
      mealSlots.map(async (slot) => {
        const randomRecipe = await getRandomRecipeFromCategory(slot.name, useOwnData);
        return {
          ...slot,
          recipe: randomRecipe,
        };
      })
    );
    setMealSlots(updatedSlots);
  };



  const selectRecipe = (index: number, selectedRecipe: RecipeData) => {
    const updated = [...mealSlots];
    updated[index] = {
      ...updated[index],
      recipe: selectedRecipe,
    };
    setMealSlots(updated);
    setActiveDropdown(null);
    setSearchTerms({ ...searchTerms, [index]: "" });
  };

  const toggleDropdown = (index: number) => {
    setActiveDropdown(activeDropdown === index ? null : index);
    if (activeDropdown !== index) {
      setSearchTerms({ ...searchTerms, [index]: "" });
    }
  };

  const handleSearchChange = (index: number, value: string) => {
    setSearchTerms({ ...searchTerms, [index]: value });
  };



  const handleViewRecipe = (recipe: RecipeData) => {
    setSelectedRecipe(recipe);
  };



  const handleCopyGroceryList = async () => {
    const groceryItems = generateGroceryList(mealSlots);
    const success = await copyGroceryListToClipboard(groceryItems);
    setCopySuccess(success);
    if (success) {
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="page loading-container">
        <div className="loading-content">
          <h2>Loading... 🔄</h2>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  // When user wants their own data but isn't logged in, show sign-in prompt
  if (!user && useOwnData) {
    console.log("User not authenticated - showing sign-in prompt");
    return (
      <div className="page">
        <header className="header">
          <h2 className="title">Meal Planner</h2>
          <div className="header-actions">
            <button 
              onClick={() => setUseOwnData(false)}
              className="use-default-recipes-btn"
            >
              Use Default Recipes
            </button>
          </div>
        </header>
        <div className="auth-prompt">
          <h3>Please sign in to access your meal planner</h3>
          <p>Your recipes and meal plans are securely stored in your personal account.</p>
        </div>
        {showAuthModal && (
          <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
            <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => setShowAuthModal(false)}
                className="close-auth-modal"
              >
                ×
              </button>
              <AuthComponent />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show loading state while recipes are loading
  if (recipesLoading) {
    return (
      <div className="page loading-container">
        <div className="loading-content">
          <h2>Loading recipes... 🔄</h2>
          <p>Fetching your recipes from the database...</p>
        </div>
      </div>
    );
  }

  // Show error state if no recipes found
  if (recipesError || !recipesData || Object.keys(recipesData).length === 0) {
    console.log("No recipes found - showing prompt to add recipes");
    return (
      <div className="page">
        <header className="header">
          <h2 className="title">Meal Planner</h2>
          <div className="header-user-section">
            {user ? (
              <span className="welcome-text">Welcome, {user.displayName || user.email}</span>
            ) : (
              <span className="welcome-text">Default Recipe Collection</span>
            )}
            <div className="header-actions">
              <button 
                onClick={handleUseOwnDataToggle}
                className={`data-toggle-btn ${useOwnData ? 'own-data' : 'default-data'}`}
              >
                {useOwnData ? 'Use Default recpies' : 'Use my own recpies'}
              </button>
              {useOwnData && (
                <>
                  <button 
                    onClick={() => setShowAddRecipe(true)}
                    className="add-btn"
                  >
                    <FaPlus /> Add Recipe
                  </button>

                </>
              )}
              {user && (
                <button 
                  onClick={handleLogout}
                  className="logout-btn"
                >
                  Log Out
                </button>
              )}
            </div>
          </div>
        </header>
        
        {recipesError && (
          <div className="error-container">
            <h3>Error loading recipes from database:</h3>
            <p>{recipesError}</p>
          </div>
        )}
        
        <div className="no-recipes-prompt">
          <h2>No recipes found in database</h2>
          <p>
            {user && useOwnData 
              ? 'Start building your personal recipe collection by adding your favorite meals.'
              : user && !useOwnData
              ? 'No default recipes are available. Switch to your own recipes or add some default ones.'
              : 'No default recipes are available. Sign in to create your own recipes or contact the administrator to add default recipes.'}
          </p>
          {user && (
            <button 
              onClick={() => setShowAddRecipe(true)}
              className="add-recipes-btn"
            >
              <FaPlus /> Add Your First Recipe
            </button>
          )}
        </div>
        
        {/* Modals */}
        {showAddRecipe && (
          <AddRecipeForm 
            onClose={() => setShowAddRecipe(false)}
            onSuccess={() => {
              window.location.reload();
            }}
          />
        )}

      </div>
    );
  }
  console.log("Rendering Meal Planner with recipes");

  return (
    <div className="page">
      {/* Header */}
      <header className="header">
        <h2 className="title">Meal Planner</h2>
        <div className="header-user-section">
        {!user && 
          (<button 
              onClick={handleUseOwnDataToggle}
              className={`data-toggle-btn ${useOwnData ? 'own-data' : 'default-data'}`}
            >
              {useOwnData ? 'Use Default recpies' : 'Use my own recpies'}
            </button>)
        }
        {
          user && (
            <span className="welcome-text">Welcome, {user?.displayName || user?.email}</span>
          )
        }
        {user && (
          <div className="header-actions">
            
              <>
                <button 
                  onClick={() => setShowManageRecipes(true)}
                  className="manage-recipes-btn"
                >
                  📋 Manage Recipes
                </button>
                <button 
                  onClick={() => setShowAddRecipe(true)}
                  className="add-btn"
                >
                  <FaPlus /> Add Recipe
                </button>

              </>
            
              <button 
                onClick={handleLogout}
                className="logout-btn"
              >
                Log Out
              </button>
          </div>
        )}
        </div>
      </header>

      {/* Meal Cards */}
      <div className="card-container">
        {mealSlots.map((slot, idx) => (
          <MealSlotCard
            key={idx}
            slot={slot}
            index={idx}
            activeDropdown={activeDropdown}
            searchTerms={searchTerms}
            onToggleDropdown={toggleDropdown}
            onSearchChange={handleSearchChange}
            onShuffleMeal={shuffleMeal}
            onViewRecipe={handleViewRecipe}
            onSelectRecipe={selectRecipe}
            useOwnData={useOwnData}
          />
        ))}
      </div>

      {/* Footer */}
      <footer className="footer mt-6 flex flex-col items-center gap-6">
        {/* Goals */}
        <div className="random-input flex gap-6 items-center">
            {/* Randomize Button */}
          <button
            onClick={randomizeDay}
            className="random-button px-6 py-2 bg-purple-500 text-white rounded-lg shadow hover:bg-purple-600"
            title="Generate a new meal plan for the day"
            type="button"
            >
            🎲 Generate My Plan
          </button>
          <button
            onClick={() => setShowGroceryList(true)}
            className="grocery-button px-6 py-2 bg-green-500 text-white rounded-lg shadow hover:bg-green-600"
            title="View grocery list for selected meals"
            type="button"
            >
            <FaShoppingCart className="inline mr-2"/>
            {" Grocery List"}
          </button>
          <div>
            <label className="block text-sm">Goal Calories:</label>
            <input
              type="number"
              value={goalCalories}
              onChange={(e) => setGoalCalories(Number(e.target.value))}
              className="border rounded p-1 w-24"
              title="Set your daily calorie goal"
            />
          </div>
          <div>
            <label className="block text-sm">Goal Protein:</label>
            <input
              type="number"
              value={goalProtein}
              onChange={(e) => setGoalProtein(Number(e.target.value))}
              className="border rounded p-1 w-24"
              title="Set your daily protein goal"
            />
          </div>
        </div>

        {/* Totals with Progress Rings */}
        <div className="rings grid flex gap-12 mt-4">
          <ProgressRing
            value={totalCalories}
            goal={goalCalories}
            color="rgb(185,233,81)" // green-400
            label="Calories"
          />
          <ProgressRing
            value={totalProtein}
            goal={goalProtein}
            color="rgb(165,79,203)" // red-400
            label="Protein"
          />
        </div>
      </footer>

      {/* Recipe Modal */}
      {selectedRecipe && (
        <Recipe 
          recipe={selectedRecipe} 
          onClose={() => setSelectedRecipe(null)} 
        />
      )}

      {/* Grocery List Modal */}
      {showGroceryList && (
        <div className="recipe-overlay" onClick={() => setShowGroceryList(false)}>
          <div className="recipe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="recipe-header">
              <h2 className="recipe-title">Grocery List</h2>
              <div className="recipe-header-buttons">
                {generateGroceryList(mealSlots).length > 0 && (
                  <button
                    className="copy-button"
                    onClick={handleCopyGroceryList}
                    title={copySuccess ? "Copied!" : "Copy grocery list to clipboard"}
                  >
                    <FaCopy />
                    {copySuccess ? " Copied!" : " Copy"}
                  </button>
                )}
                <button 
                  className="recipe-close-btn" 
                  onClick={() => setShowGroceryList(false)}
                  aria-label="Close grocery list"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="recipe-content">
              {generateGroceryList(mealSlots).length > 0 ? (
                <ul className="ingredients-list">
                  {generateGroceryList(mealSlots).map((item, index) => (
                    <li key={index} className="ingredient-item">
                      <div className="ingredient-main">
                        <span className="ingredient-quantity">
                          {item.totalQuantity} {item.unit}
                        </span>
                        <span className="ingredient-name"> {item.name}</span>
                      </div>
                      <div className="ingredient-nutrition">
                        <span className="ingredient-meals">
                          {item.meals.join(', ')}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No meals selected. Choose some meals to generate your grocery list!
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
        {/* Modals */}
        {showAddRecipe && (
          <AddRecipeForm 
            onClose={() => setShowAddRecipe(false)}
            onSuccess={() => {
              // Refresh recipes after adding
              window.location.reload();
            }}
          />
        )}
        
        {/* Manage Recipes Modal */}
        {showManageRecipes && (
          <div className="recipe-overlay" onClick={() => setShowManageRecipes(false)}>
            <div className="recipe-modal manage-recipes-modal" onClick={(e) => e.stopPropagation()}>
              <div className="recipe-header">
                <h2 className="recipe-title">Manage My Recipes</h2>
                <button 
                  className="recipe-close-btn" 
                  onClick={() => setShowManageRecipes(false)}
                  aria-label="Close manage recipes"
                >
                  ×
                </button>
              </div>
              
              <div className="recipe-content">
                {/* Meal Filter */}
                <div className="meal-filter">
                  <label htmlFor="meal-filter">Filter by meal type:</label>
                  <select 
                    id="meal-filter"
                    value={mealFilter} 
                    onChange={(e) => setMealFilter(e.target.value)}
                    className="meal-filter-select"
                  >
                    <option value="all">All Meals</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="snack">Snack</option>
                    <option value="dinner">Dinner</option>
                  </select>
                </div>

                {/* Recipes List */}
                <div className="recipes-manage-list">
                  {recipesData && Object.values(recipesData)
                    .flat()
                    .filter((recipe) => {
                      const typedRecipe = recipe as RecipeData;
                      return mealFilter === 'all' || typedRecipe.meal === mealFilter;
                    })
                    .map((recipe) => {
                      const typedRecipe = recipe as RecipeData;
                      const isExpanded = expandedRecipe === typedRecipe.id;
                      const isEditing = editingRecipe === typedRecipe.id;
                      
                      return (
                        <div key={typedRecipe.id} className="recipe-manage-item">
                          {/* Recipe Header Row */}
                          <div 
                            className="recipe-row-header"
                            onClick={() => setExpandedRecipe(isExpanded ? null : typedRecipe.id)}
                          >
                            <div className="recipe-row-info">
                              <span className="recipe-row-name">{typedRecipe.title}</span>
                              <span className="recipe-row-meal">{typedRecipe.meal}</span>
                            </div>
                            <button className="expand-btn">
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="recipe-expanded-content">
                              {!isEditing ? (
                                // View Mode
                                <div className="recipe-details">
                                  <div className="recipe-detail-section">
                                    <h4>Nutrition Information</h4>
                                    <p><strong>Calories:</strong> {typedRecipe.calories} kcal</p>
                                    <p><strong>Protein:</strong> {typedRecipe.protein}g</p>
                                  </div>
                                  
                                  <div className="recipe-detail-section">
                                    <h4>Ingredients</h4>
                                    <ul className="ingredient-list">
                                      {typedRecipe.ingredients?.map((ingredient, idx) => (
                                        <li key={idx} className="ingredient-item-detail">
                                          {ingredient.quantity} {ingredient.unit} {ingredient.name}
                                          {ingredient.calories && ` (${ingredient.calories} kcal)`}
                                          {ingredient.protein && ` (${ingredient.protein}g protein)`}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>

                                  {typedRecipe.instructions && (
                                    <div className="recipe-detail-section">
                                      <h4>Instructions</h4>
                                      <p className="instructions-text">{typedRecipe.instructions}</p>
                                    </div>
                                  )}

                                  {typedRecipe.link && (
                                    <div className="recipe-detail-section">
                                      <h4>Recipe Link</h4>
                                      <a 
                                        href={typedRecipe.link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="recipe-link"
                                      >
                                        View Original Recipe
                                      </a>
                                    </div>
                                  )}

                                  <div className="recipe-actions">
                                    <button 
                                      onClick={() => {
                                        setEditingRecipe(typedRecipe.id);
                                        setEditFormData(typedRecipe);
                                      }}
                                      className="edit-recipe-btn"
                                    >
                                      ✏️ Edit Recipe
                                    </button>
                                    <button 
                                      onClick={() => {
                                        // TODO: Implement delete functionality
                                        console.log('Delete recipe:', typedRecipe.id);
                                      }}
                                      className="delete-recipe-btn"
                                    >
                                      🗑️ Delete Recipe
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // Edit Mode
                                <div className="recipe-edit-form">
                                  <div className="edit-form-section">
                                    <label>Recipe Name:</label>
                                    <input 
                                      type="text"
                                      value={editFormData.title || ''}
                                      onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                                      className="edit-input"
                                      placeholder="Recipe name"
                                      title="Recipe name"
                                    />
                                  </div>

                                  <div className="edit-form-section">
                                    <label>Meal Type:</label>
                                    <select 
                                      value={editFormData.meal || ''}
                                      onChange={(e) => setEditFormData({...editFormData, meal: e.target.value as 'breakfast' | 'lunch' | 'snack' | 'dinner'})}
                                      className="edit-select"
                                      title="Select meal type"
                                    >
                                      <option value="breakfast">Breakfast</option>
                                      <option value="lunch">Lunch</option>
                                      <option value="snack">Snack</option>
                                      <option value="dinner">Dinner</option>
                                    </select>
                                  </div>

                                  <div className="edit-form-row">
                                    <div className="edit-form-section">
                                      <label>Calories:</label>
                                      <input 
                                        type="number"
                                        value={editFormData.calories || ''}
                                        onChange={(e) => setEditFormData({...editFormData, calories: Number(e.target.value)})}
                                        className="edit-input"
                                        placeholder="0"
                                        title="Calories per serving"
                                      />
                                    </div>
                                    <div className="edit-form-section">
                                      <label>Protein (g):</label>
                                      <input 
                                        type="number"
                                        value={editFormData.protein || ''}
                                        onChange={(e) => setEditFormData({...editFormData, protein: Number(e.target.value)})}
                                        className="edit-input"
                                        placeholder="0"
                                        title="Protein per serving in grams"
                                      />
                                    </div>
                                  </div>

                                  <div className="edit-form-section">
                                    <label>Recipe Link (optional):</label>
                                    <input 
                                      type="url"
                                      value={editFormData.link || ''}
                                      onChange={(e) => setEditFormData({...editFormData, link: e.target.value})}
                                      className="edit-input"
                                      placeholder="https://example.com/recipe"
                                    />
                                  </div>

                                  <div className="edit-form-section">
                                    <label>Instructions (optional):</label>
                                    <textarea 
                                      value={editFormData.instructions || ''}
                                      onChange={(e) => setEditFormData({...editFormData, instructions: e.target.value})}
                                      className="edit-textarea"
                                      rows={4}
                                      placeholder="Cooking instructions..."
                                    />
                                  </div>

                                  <div className="edit-actions">
                                    <button 
                                      onClick={() => {
                                        // TODO: Implement save functionality
                                        console.log('Save recipe:', editFormData);
                                        setEditingRecipe(null);
                                        setEditFormData({});
                                      }}
                                      className="save-recipe-btn"
                                    >
                                      💾 Save Changes
                                    </button>
                                    <button 
                                      onClick={() => {
                                        setEditingRecipe(null);
                                        setEditFormData({});
                                      }}
                                      className="cancel-edit-btn"
                                    >
                                      ❌ Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        {showAuthModal && (
          <div 
            className="auth-modal-overlay" 
            onClick={() => setShowAuthModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div 
              className="auth-modal-content" 
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '20px',
                maxWidth: '400px',
                width: '90%',
                position: 'relative',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}
            >
              <button 
                onClick={() => setShowAuthModal(false)}
                className="close-auth-modal"
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
              <AuthComponent />
            </div>
          </div>
        )}
      </div>
    );
  }